import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  QueryKey,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { HTTPInstance } from '../core';
import { ApiResponse, ErrorResponse } from '../types/api';

export const useApiQuery = <TData>(
  queryKey: QueryKey,
  url: string,
  options?: Partial<UseQueryOptions<ApiResponse<TData>, ErrorResponse>>,
) => {
  const { enqueueSnackbar } = useSnackbar();
  return useQuery<ApiResponse<TData>, ErrorResponse>({
    queryKey,
    queryFn: async () => {
      try {
        const response = await HTTPInstance.get<ApiResponse<TData>>(url);
        return response.data;
      } catch (error: any) {
        enqueueSnackbar(
          `We had trouble loading this. Please try again in a moment.`,
          {
            variant: 'error',
          },
        );
        throw error;
      }
    },
    ...options,
  });
};

export const useApiMutation = <TData, TVariables>(
  url: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  options?: UseMutationOptions<ApiResponse<TData>, ErrorResponse, TVariables>,
) => {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<ApiResponse<TData>, ErrorResponse, TVariables>({
    mutationFn: async (variables: TVariables) => {
      try {
        const isFormData = variables instanceof FormData;
        const response = await HTTPInstance.request<ApiResponse<TData>>({
          url,
          method,
          data: variables,
          headers: isFormData
            ? { 'Content-Type': 'multipart/form-data' }
            : { 'Content-Type': 'application/json' },
        });
        return response.data;
      } catch (error: any) {
        enqueueSnackbar(
          `Something didn't go as planned. Please try again in a moment.`,
          {
            variant: 'error',
          },
        );
        throw error;
      }
    },
    onSuccess: (data: ApiResponse<TData>) => {
      enqueueSnackbar(
        data.message || 'All done — your changes have been saved.',
        {
          variant: 'success',
        },
      );
    },
    ...options,
  });
};
